import React, { useState } from 'react'
import {
  Upload,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Typography,
  message,
  Spin,
} from 'antd'
import {
  InboxOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
} from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchMlPrediction } from '$api/evoAPIs'
import { StyledLayout } from '$globalStyledComponents/Layout'
import Footer from '$components/Footer'

const { Content } = StyledLayout
const { Dragger } = Upload
const { Title, Text } = Typography

const MlLab = () => {
  const { t } = useTranslation()
  const [prediction, setPrediction] = useState(null)

  const mutation = useMutation(fetchMlPrediction, {
    onSuccess: (data) => {
      setPrediction(data)
      message.success(t('ml_lab.analysis_complete'))
    },
    onError: (err) => {
      console.error(err)
      message.error(t('ml_lab.analysis_failed'))
    },
  })

  const handleUpload = ({ file, onSuccess }) => {
    setPrediction(null)
    mutation.mutate(file)
    // Antd upload expects a success callback immediately for UI feedback,
    // even though our real processing happens in mutation
    setTimeout(() => onSuccess('ok'), 0)
  }

  return (
    <StyledLayout className="layout">
      <Content>
        <div style={{ margin: '50px 0', textAlign: 'center' }}>
          <Title level={1}>
            <RobotOutlined /> {t('ml_lab.title')}
          </Title>
          <Text type="secondary">{t('ml_lab.description')}</Text>
        </div>

        <Row justify="center">
          <Col xs={24} md={16} lg={12}>
            <Card style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <Dragger
                customRequest={handleUpload}
                showUploadList={false}
                accept=".stl"
                disabled={mutation.isLoading}
              >
                <p className="ant-upload-drag-icon">
                  {mutation.isLoading ? (
                    <Spin size="large" />
                  ) : (
                    <InboxOutlined />
                  )}
                </p>
                <p className="ant-upload-text">{t('ml_lab.upload_text')}</p>
                <p className="ant-upload-hint">{t('ml_lab.upload_hint')}</p>
              </Dragger>
            </Card>
          </Col>
        </Row>

        {prediction && (
          <div style={{ marginTop: '40px' }}>
            <Row gutter={[24, 24]} justify="center">
              {/* Feasibility Card */}
              <Col xs={24} md={8}>
                <Card
                  title={t('ml_lab.feasibility_title')}
                  bordered={false}
                  style={{ height: '100%', textAlign: 'center' }}
                >
                  <div style={{ marginBottom: 20 }}>
                    {prediction.is_printable ? (
                      <CheckCircleOutlined
                        style={{ fontSize: '48px', color: '#52c41a' }}
                      />
                    ) : (
                      <CloseCircleOutlined
                        style={{ fontSize: '48px', color: '#ff4d4f' }}
                      />
                    )}
                  </div>
                  <Title level={3}>
                    {prediction.is_printable
                      ? t('ml_lab.printable')
                      : t('ml_lab.not_printable')}
                  </Title>
                  <Tag
                    color={prediction.is_printable ? 'green' : 'red'}
                    style={{ fontSize: '14px', padding: '5px 10px' }}
                  >
                    {t('ml_lab.confidence')}:{' '}
                    {(prediction.confidence * 100).toFixed(1)}%
                  </Tag>
                </Card>
              </Col>

              {/* Pricing Card */}
              <Col xs={24} md={8}>
                <Card
                  title={t('ml_lab.price_title')}
                  bordered={false}
                  style={{ height: '100%', textAlign: 'center' }}
                >
                  <div style={{ marginBottom: 20 }}>
                    <DollarOutlined
                      style={{ fontSize: '48px', color: '#1890ff' }}
                    />
                  </div>
                  <Statistic
                    value={prediction.estimated_price_eur}
                    precision={2}
                    prefix="€"
                    valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                  />
                  <Text type="secondary">{t('ml_lab.price_subtitle')}</Text>
                </Card>
              </Col>

              {/* Features Card */}
              <Col xs={24} md={8}>
                <Card
                  title={t('ml_lab.features_title')}
                  bordered={false}
                  style={{ height: '100%' }}
                >
                  <ul style={{ paddingLeft: 20 }}>
                    <li>
                      <b>{t('ml_lab.volume')}:</b>{' '}
                      {prediction.features.volume.toFixed(2)} mm³
                    </li>
                    <li>
                      <b>{t('ml_lab.area')}:</b>{' '}
                      {prediction.features.area.toFixed(2)} mm²
                    </li>
                    <li>
                      <b>{t('ml_lab.faces')}:</b>{' '}
                      {prediction.features.num_faces}
                    </li>
                    <li>
                      <b>{t('ml_lab.packing_factor')}:</b>{' '}
                      {prediction.features.packing_factor.toFixed(3)}
                    </li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Content>
      <Footer />
    </StyledLayout>
  )
}

export default MlLab
